import * as React from "react"
import { useQuery } from "@tanstack/react-query"
import { useSearchParams } from "react-router-dom"
import { FileSearch } from "lucide-react"

import { DataState } from "@/components/DataState"
import { Toolbar, ToolbarField } from "@/components/Toolbar"
import { WorkPanel } from "@/components/WorkPanel"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useAuth } from "@/lib/auth"
import { formatAmount, formatDate, formatDateTime } from "@/lib/format"
import { useI18n, t } from "@/lib/i18n"
import { BOOK_LABELS } from "@/lib/types"
import { useTableKeyboardNav } from "@/lib/useTableKeyboardNav"
import { cn } from "@/lib/utils"

import { fetchAccounts, fetchEntry, fetchLedgerAccount } from "@/routes/books/api"
import { useBooksFilters } from "@/routes/books/context"

/**
 * EL MAYOR, CON EL ASIENTO A UN CLIC.
 *
 * Había dos pestañas sobre la misma consulta —`/ledger/ledger/{cuenta}`—: «Mayor»
 * traía el saldo de apertura y el saldo arrastrado línea a línea, y «Análisis de
 * cuenta» traía las mismas filas con un botón «Ver asiento» que abría el
 * asiento completo. Dos pantallas para una pregunta, y el enlace que iba de una
 * a otra (`/books/analisis?cuenta=`) caía en el diario porque esa ruta ya no
 * existía. Ahora es una: el mayor, con su saldo, y cada fila abre su asiento.
 *
 * Lo que NO se trajo del análisis, y por qué: la «composición por centro de
 * costo» siempre daba un solo bloque —el mayor no trae el centro de costo, así
 * que todo caía en «sin CC»— y la columna «Origen» siempre era un guion. Un
 * panel que enseña siempre lo mismo no es información.
 *
 * La cuenta viaja en la URL (`?cuenta=`): el balance de comprobación enlaza aquí
 * desde cada código.
 */
export function LedgerPage() {
  const { book, from, to } = useBooksFilters()
  const { activeTenantId } = useAuth()
  const { t } = useI18n()
  const [searchParams, setSearchParams] = useSearchParams()
  const [entryId, setEntryId] = React.useState<string | null>(null)

  const accountsQuery = useQuery({
    queryKey: ["accounts", activeTenantId],
    queryFn: fetchAccounts,
  })

  const accounts = React.useMemo(() => accountsQuery.data ?? [], [accountsQuery.data])

  // Sin cuenta elegida se toma la primera del catalogo, que si existe siempre.
  // Antes arrancaba en un codigo fijo que existia en los mocks y no en el plan
  // real, y el mayor abria vacio sin decir por que.
  const accountCode = searchParams.get("cuenta") ?? accounts[0]?.code ?? ""
  const elegirCuenta = (value: string) => setSearchParams({ cuenta: value }, { replace: true })

  const account = accounts.find((item) => item.code === accountCode)

  const ledgerQuery = useQuery({
    queryKey: ["ledger", activeTenantId, book, accountCode, from, to],
    queryFn: () => fetchLedgerAccount({ accountCode, book, from, to, accountName: account?.name }),
    enabled: Boolean(accountCode),
  })

  const entryQuery = useQuery({
    queryKey: ["entry", entryId],
    queryFn: () => fetchEntry(entryId as string),
    enabled: Boolean(entryId),
  })

  const ledger = ledgerQuery.data
  const movements = ledger?.movements ?? []
  const nav = useTableKeyboardNav({
    count: movements.length,
    onActivate: (index) => {
      const m = movements[index]
      if (m) setEntryId(m.entryId)
    },
  })

  const totalDebit = movements.reduce((sum, m) => sum + m.debit, 0)
  const totalCredit = movements.reduce((sum, m) => sum + m.credit, 0)

  return (
    <div className="space-y-3">
      <Toolbar>
        <ToolbarField label={t("Cuenta")} htmlFor="cuenta-mayor" className="min-w-[24rem] flex-1">
          <Select value={accountCode} onValueChange={elegirCuenta}>
            <SelectTrigger id="cuenta-mayor" className="w-full max-w-[34rem]">
              <SelectValue placeholder={t("Selecciona una cuenta")} />
            </SelectTrigger>
            <SelectContent>
              {accounts.map((item) => (
                <SelectItem key={item.code} value={item.code}>
                  <span className="flex items-baseline gap-2">
                    <span className="code w-14 shrink-0 font-medium">{item.code}</span>
                    <span className="truncate text-muted-foreground">{item.name}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </ToolbarField>
      </Toolbar>

      {/* Cifras de la cuenta: apertura, movimiento y cierre en una banda. */}
      <div className="flex flex-wrap items-stretch divide-x divide-border rounded-md border bg-surface">
        <div className="min-w-0 flex-1 px-3 py-2">
          <div className="eyebrow">{t("cuenta")}</div>
          <div className="mt-0.5 flex items-baseline gap-2">
            <span className="code text-md font-semibold">{accountCode}</span>
            <span className="truncate text-xs text-muted-foreground">{account?.name ?? ""}</span>
          </div>
        </div>
        <Figure label={`${t("saldo")} inicial`} value={ledger?.openingBalance ?? 0} />
        <Figure label={`Movim. ${t("debe")}`} value={totalDebit} muted />
        <Figure label={`Movim. ${t("haber")}`} value={totalCredit} muted />
        <Figure label={`${t("saldo")} final`} value={ledger?.closingBalance ?? 0} emphasis />
      </div>

      <WorkPanel
        title={t("libro_mayor")}
        meta={
          <span className="text-2xs text-muted-foreground">
            {formatDate(from)} — {formatDate(to)}
          </span>
        }
        footnote={t("El saldo se arrastra linea a linea desde la apertura: la ultima fila del cuerpo y el saldo final del pie tienen que coincidir siempre. Enter (o «Ver asiento») abre el asiento completo con su autor, su motivo y sus contrapartidas.")}
      >
        <DataState
          loading={ledgerQuery.isLoading}
          error={ledgerQuery.error}
          onRetry={() => void ledgerQuery.refetch()}
          empty={movements.length === 0}
          emptyMessage={t("La cuenta no tiene movimientos en el rango.")}
          emptyHint={t("Cambia el rango de fechas o elige otra cuenta. El saldo de apertura sigue siendo valido aunque no haya movimiento.")}
          columns={[8, 10, 40, 12, 10, 10, 10]}
        >
          <Table containerClassName="max-h-[calc(100vh-24rem)]">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[5.5rem]">{t("Fecha")}</TableHead>
                <TableHead className="w-28">{t("Asiento")}</TableHead>
                <TableHead className="min-w-[20rem]">{t("Glosa")}</TableHead>
                <TableHead className="w-32">{t("Documento")}</TableHead>
                <TableHead className="w-28 text-right">{t("debe")}</TableHead>
                <TableHead className="w-28 text-right">{t("haber")}</TableHead>
                <TableHead className="w-32 text-right">{t("saldo")}</TableHead>
                <TableHead className="w-28" />
              </TableRow>
            </TableHeader>
            <TableBody {...nav.bodyProps}>
              <TableRow className="row-group hover:bg-muted/60">
                <TableCell
                  colSpan={4}
                  className="text-2xs font-semibold uppercase tracking-[0.07em] text-muted-foreground"
                >
                  {t("Saldo de apertura al")}{" "}{formatDate(from)}
                </TableCell>
                <TableCell className="num-zero">—</TableCell>
                <TableCell className="num-zero">—</TableCell>
                <TableCell className="num-strong">
                  {formatAmount(ledger?.openingBalance ?? 0)}
                </TableCell>
                <TableCell />
              </TableRow>

              {movements.map((movement, index) => (
                <TableRow
                  key={movement.id}
                  {...nav.rowProps(index)}
                  className={cn("outline-none", nav.cursor === index && "row-cursor")}
                >
                  <TableCell className="code text-muted-foreground">
                    {formatDate(movement.date)}
                  </TableCell>
                  <TableCell className="code font-medium">{movement.entryNumber}</TableCell>
                  <TableCell className="max-w-0 truncate" title={movement.glosa}>
                    {movement.glosa}
                  </TableCell>
                  <TableCell className="code text-2xs text-muted-foreground">
                    {movement.documentRef ?? "—"}
                  </TableCell>
                  <TableCell className={cn(movement.debit ? "num" : "num-zero")}>
                    {formatAmount(movement.debit, { blankZero: true }) || "—"}
                  </TableCell>
                  <TableCell className={cn(movement.credit ? "num" : "num-zero")}>
                    {formatAmount(movement.credit, { blankZero: true }) || "—"}
                  </TableCell>
                  <TableCell className="num font-medium">{formatAmount(movement.balance)}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="xs"
                      variant="ghost"
                      className="opacity-70 transition-opacity group-hover/row:opacity-100 group-focus-within/row:opacity-100"
                      onClick={() => setEntryId(movement.entryId)}
                    >
                      <FileSearch />
                      {t("Ver asiento")}</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell colSpan={4} className="text-2xs uppercase tracking-[0.07em]">
                  {t("Totales del periodo")}</TableCell>
                <TableCell className="num">{formatAmount(totalDebit)}</TableCell>
                <TableCell className="num">{formatAmount(totalCredit)}</TableCell>
                <TableCell className="num">{formatAmount(ledger?.closingBalance ?? 0)}</TableCell>
                <TableCell />
              </TableRow>
            </TableFooter>
          </Table>
        </DataState>
      </WorkPanel>

      <Dialog open={Boolean(entryId)} onOpenChange={(open) => !open && setEntryId(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              {t("Asiento")}{" "}{entryQuery.data?.number ?? "…"} · {BOOK_LABELS[book]}
            </DialogTitle>
            <DialogDescription>
              {entryQuery.data
                ? `${entryQuery.data.glosa} · ${formatDate(entryQuery.data.date)} · ${
                    entryQuery.data.documentRef ?? "sin documento"
                  }`
                : "Cargando el asiento…"}
            </DialogDescription>
          </DialogHeader>

          {entryQuery.data ? (
            <>
              <div className="overflow-hidden rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-24">{t("cuenta")}</TableHead>
                      <TableHead>{t("Denominacion")}</TableHead>
                      <TableHead className="w-20">{t("C. costo")}</TableHead>
                      <TableHead className="w-28 text-right">{t("debe")}</TableHead>
                      <TableHead className="w-28 text-right">{t("haber")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entryQuery.data.lines.map((line) => (
                      <TableRow key={line.id}>
                        <TableCell className="code font-medium">{line.accountCode}</TableCell>
                        <TableCell>{line.accountName}</TableCell>
                        <TableCell className="code text-2xs text-muted-foreground">
                          {line.costCenter ?? "—"}
                        </TableCell>
                        <TableCell className={cn(line.debit ? "num" : "num-zero")}>
                          {formatAmount(line.debit, { blankZero: true }) || "—"}
                        </TableCell>
                        <TableCell className={cn(line.credit ? "num" : "num-zero")}>
                          {formatAmount(line.credit, { blankZero: true }) || "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TableCell colSpan={3} className="text-2xs uppercase tracking-[0.07em]">
                        {t("Totales")}</TableCell>
                      <TableCell className="num">
                        {formatAmount(entryQuery.data.lines.reduce((s, l) => s + l.debit, 0))}
                      </TableCell>
                      <TableCell className="num">
                        {formatAmount(entryQuery.data.lines.reduce((s, l) => s + l.credit, 0))}
                      </TableCell>
                    </TableRow>
                  </TableFooter>
                </Table>
              </div>

              <dl className="grid gap-x-4 gap-y-1 rounded-md border bg-surface-sunken p-2.5 text-2xs sm:grid-cols-[7rem_minmax(0,1fr)]">
                <dt className="uppercase tracking-[0.06em] text-muted-foreground">{t("Autor")}</dt>
                <dd>
                  {entryQuery.data.createdBy}
                  <span className="ml-1.5 text-muted-foreground">
                    {formatDateTime(entryQuery.data.createdAt)}
                  </span>
                </dd>
                <dt className="uppercase tracking-[0.06em] text-muted-foreground">{t("Origen")}</dt>
                <dd>{entryQuery.data.origin}</dd>
                {entryQuery.data.reason ? (
                  <>
                    <dt className="uppercase tracking-[0.06em] text-muted-foreground">{t("Motivo")}</dt>
                    <dd>{entryQuery.data.reason}</dd>
                  </>
                ) : null}
              </dl>
            </>
          ) : (
            <div className="space-y-1.5 py-6">
              <div className="h-3 w-2/3 animate-pulse rounded bg-muted" />
              <div className="h-3 w-1/2 animate-pulse rounded bg-muted" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

function Figure({
  label,
  value,
  muted,
  emphasis,
}: {
  label: string
  value: number
  muted?: boolean
  emphasis?: boolean
}) {
  return (
    <div className="min-w-[7.5rem] px-3 py-2">
      <div className="eyebrow">{label}</div>
      <div
        className={cn(
          "num mt-0.5 text-left tabular-nums",
          emphasis ? "text-md font-semibold" : "text-sm",
          muted && "text-muted-foreground",
          emphasis && value < 0 && "text-destructive",
        )}
      >
        {formatAmount(value)}
      </div>
    </div>
  )
}
